import { Injectable } from '@angular/core';
import { NSProvider, NSProviderType } from '@shared/models/navigation-source.const';
import { QueryPayload, StaticPayloadParams } from '@shared/models/navigation-source.model';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';

@Injectable({ providedIn: 'root' })
export class NavigationSourceFactoryService {
  constructor(private userInfo: UserInfoResolver) {}
  get sessionStorage(): string {
    return window.sessionStorage.getItem(NSProvider.NavigationSource);
  }

  set sessionStorage(payload: string) {
    window.sessionStorage.setItem(NSProvider.NavigationSource, payload);
  }

  get localStorage(): string {
    const storedData = window.localStorage.getItem(NSProvider.NavigationSource);
    window.localStorage.removeItem(NSProvider.NavigationSource);
    return storedData;
  }

  set localStorage(payload: string) {
    window.localStorage.setItem(NSProvider.NavigationSource, payload);
  }

  /**
   * Checks session and local storage if any 'navigation-source' is set up for the entity being passed.
   * @param entity check if the session storage has any query for this entity
   * @returns QueryPayload with with data has to be fetched if the
   */
  public getNavigationSourceProviderByType(entity: string): QueryPayload {
    // Check if the sessionStorage has any 'navigation-source' saved.
    let sessionStorage = JSON.parse(this.sessionStorage);
    // Check if the localStorage has any 'navigation-source' saved.
    const localStorage = JSON.parse(this.localStorage);
    // If sessionStorage is not found, then try to populate it from localStorage
    if (sessionStorage == null) {
      if (localStorage == null) {
        return null;
      }
      // if 'navigation-source' is found in localStorage then save it to sessionStorage.
      this.sessionStorage = JSON.stringify(localStorage);
      sessionStorage = localStorage;
    }
    // If the entity user is on the same saved in sessionStorage then return the query else null.
    return sessionStorage?.entity === entity || sessionStorage?.relatedEntity === entity ? sessionStorage : null;
  }

  /**
   * Used to store static type of navigation. Entity and record ids are needed
   * to built the query and save it in local and session storage.
   * @param payloadParams contains the entity to navigate and ids of the records
   */
  public saveStaticNavigationSource(payloadParams: StaticPayloadParams): void {
    const { entity, ids } = payloadParams;
    const searchRequest = {
      entity,
      navigationSource: NSProviderType.Static,
      filters: [
        {
          dataType: 'id',
          field: 'entity.id',
          type: 'multi',
          values: ids,
        },
      ],
      userId: this.userInfo?.resolve()?.id,
      offset: 0,
      limit: ids.length,
    };
    this.#savePayloadToStorage(searchRequest);
  }

  /**
   * Used to store async type of navigation. QueryPayload is required to save it in local and session storage.
   * @param payload contains the search params.
   */
  public saveAsyncNavigationSource(payload: Partial<QueryPayload>): void {
    this.#savePayloadToStorage({
      ...payload,
      navigationSource: NSProviderType.Async,
    });
  }

  /**
   * When the navigation happens from records-list in search landing page then remove the session storage of that entity.
   * @param entity from what entity we are navigating and
   */
  public clearSessionStorageForEntity(entity: string) {
    const sessionStorage = JSON.parse(this.sessionStorage);
    window.localStorage.removeItem(NSProvider.NavigationSource);
    if (sessionStorage?.entity === entity) {
      window.sessionStorage.clear();
    }
  }

  /**
   * Saves the query directly to the storage. Doesn't add any additonal information.
   * @param payload to be saved to session and local storage.
   */
  public saveNavigationSourceProviderByType(payload: QueryPayload) {
    this.#savePayloadToStorage(payload);
  }

  /**
   * Saves the payload to local and session storage by converting it to string
   * @param payload to be saved in the storage.
   */
  #savePayloadToStorage(payload: Partial<QueryPayload>): void {
    this.sessionStorage = JSON.stringify(payload);
    this.localStorage = JSON.stringify(payload);
  }
}
